import type {
  WeightProfile,
  DimensionScores,
  DimensionKey,
  LandmarkTier,
  DetractorConfig,
  DiagnosticBlockingFactor,
  DiagnosticSuggestion,
  DiagnosticCardResult,
  PrismOwnerBaseline,
  PrismPreferenceVector,
} from '@frame/scoring-types';
import {
  LANDMARK_COEFFICIENTS,
  DEFAULT_DETRACTOR_CONFIG,
} from '@frame/scoring-types';

export function normalizeWeights(profile: WeightProfile): WeightProfile {
  const total =
    profile.portraitWeight + profile.lightingWeight +
    profile.compositionWeight + profile.sceneWeight;
  if (total <= 0) return profile;
  return {
    ...profile,
    portraitWeight: profile.portraitWeight / total,
    lightingWeight: profile.lightingWeight / total,
    compositionWeight: profile.compositionWeight / total,
    sceneWeight: profile.sceneWeight / total,
  };
}

/** Get effective weights, redistributing portrait weight if no face detected. */
export function getEffectiveWeights(
  profile: WeightProfile,
  isLandscape: boolean,
): Record<DimensionKey, number> {
  const norm = normalizeWeights(profile);
  const weights: Record<DimensionKey, number> = {
    portraitComposition: norm.portraitWeight,
    lighting: norm.lightingWeight,
    composition: norm.compositionWeight,
    sceneInterest: norm.sceneWeight,
  };

  if (isLandscape && weights.portraitComposition > 0) {
    const fw = weights.portraitComposition;
    weights.portraitComposition = 0;
    weights.sceneInterest += fw * 0.50;
    weights.lighting += fw * 0.33;
    weights.composition += fw * 0.17;

    // Renormalize
    const t = weights.lighting + weights.composition + weights.sceneInterest;
    if (t > 0) {
      weights.lighting /= t; weights.composition /= t; weights.sceneInterest /= t;
    }
  }

  return weights;
}

// Patent: Claim 3 — Landmark spike coefficient system.
// Applies tier-based multiplicative coefficient to scene interest score.
// Tiers: Iconic (1.5×), Notable (1.25×), Interesting (1.1×), Ordinary (1.0×).
/** Apply landmark spike to scene interest score. */
export function applyLandmarkSpike(
  baseScene: number,
  tier: LandmarkTier,
  spikeStrength: number,
): number {
  const coefficient = LANDMARK_COEFFICIENTS[tier];
  const spiked = baseScene + (coefficient - 1.0) * spikeStrength * baseScene;
  return Math.min(spiked, 10.0);
}

// Patent: Claim 1 — Transparent composite scoring system.
// Computes weighted sum of per-dimension scores with mode-specific weight profiles.
// Patent: Claim 4 — v2 Subtractive detractor penalty system with diminishing returns.
// composite_adjusted = max(floor, composite - total_subtraction)
/** Compute the final composite score from dimension scores + profile. */
export function computeComposite(
  dimensions: DimensionScores,
  profile: WeightProfile,
  detractorSubtraction: number = 0,
  isPortrait: boolean = true,
): { rawComposite: number; finalComposite: number } {
  const isLandscape = dimensions.portraitComposition === null;
  const weights = getEffectiveWeights(profile, isLandscape);

  let weightedSum = 0;
  let totalWeight = 0;

  if (dimensions.portraitComposition !== null && weights.portraitComposition > 0) {
    weightedSum += dimensions.portraitComposition * weights.portraitComposition;
    totalWeight += weights.portraitComposition;
  }

  weightedSum += dimensions.lighting * weights.lighting;
  totalWeight += weights.lighting;

  weightedSum += dimensions.composition * weights.composition;
  totalWeight += weights.composition;

  const sceneScore = dimensions.sceneInterestSpiked;
  weightedSum += sceneScore * weights.sceneInterest;
  totalWeight += weights.sceneInterest;

  const rawComposite = totalWeight > 0 ? weightedSum / totalWeight : 0;

  // v2: Subtractive penalties — floor at 2.0 for portraits (maps to FRAME 4.0), 0.0 for non-portraits
  const floor = isPortrait ? 2.0 : 0.0;
  const penalized = profile.detractorPenaltyEnabled
    ? Math.max(floor, rawComposite - detractorSubtraction)
    : rawComposite;
  const finalComposite = Math.round(penalized * 10) / 10;

  return { rawComposite, finalComposite };
}

// Patent: Claim 4(c) — Crowd-based detractor penalty.
// Applies progressive penalty based on detected face count.
/** Compute crowd penalty from face count. */
export function computeCrowdPenalty(
  faceCount: number,
  config: DetractorConfig = DEFAULT_DETRACTOR_CONFIG,
): number {
  for (let i = config.crowdThresholds.length - 1; i >= 0; i--) {
    if (faceCount >= config.crowdThresholds[i].faces) {
      return config.crowdThresholds[i].penalty;
    }
  }
  return 0;
}

// ─── PATENT CLAIM: Weakest-Dimension-First Edit Prioritization ──
// No prior art for this specific logic (see prior art report).
// Walmart US11,688,049 does threshold-based correction per dimension.
// NIMA uses score-as-loss for general enhancement.
// FRAME's innovation: automated identification of weakest dimension,
// editing targeted to that dimension with intensity PROPORTIONAL to
// the score deficit from a quality threshold.

export interface EditPriority {
  dimension: DimensionKey;
  score: number;
  deficit: number;         // threshold - score (higher = more editing needed)
  editIntensity: number;   // 0-1, proportional to deficit
  editOperations: string[];
  priorityRank: number;    // 1 = weakest, edit first
}

export interface EditPlan {
  priorities: EditPriority[];
  totalDeficit: number;
  weakestDimension: DimensionKey;
  strongestDimension: DimensionKey;
  summary: string;
}

const EDIT_OPERATIONS: Record<DimensionKey, string[]> = {
  portraitComposition: ['face_relight', 'skin_smooth', 'eye_brighten', 'background_blur'],
  lighting: ['exposure_correct', 'shadow_recovery', 'highlight_recovery', 'white_balance', 'contrast_enhance'],
  composition: ['auto_crop', 'horizon_level', 'vignette_add', 'distraction_remove'],
  sceneInterest: ['vibrance_boost', 'clarity_enhance', 'color_grade', 'sky_enhance', 'detail_sharpen'],
};

/**
 * Generate an edit plan that targets the weakest dimension first
 * with intensity proportional to the score deficit.
 * 
 * This is the core of FRAME's score-card-driven editing pipeline.
 * The score card IS the edit recipe.
 * 
 * @param dimensions - The per-dimension scores from scoring
 * @param qualityThreshold - Target quality level (default 7.5)
 * @returns EditPlan with prioritized edit operations
 */
export function generateEditPlan(
  dimensions: DimensionScores,
  qualityThreshold: number = 7.5,
): EditPlan {
  // Build deficit map for all active dimensions
  const entries: Array<{ key: DimensionKey; score: number }> = [];

  if (dimensions.portraitComposition !== null) {
    entries.push({ key: 'portraitComposition', score: dimensions.portraitComposition });
  }
  entries.push({ key: 'lighting', score: dimensions.lighting });
  entries.push({ key: 'composition', score: dimensions.composition });
  entries.push({ key: 'sceneInterest', score: dimensions.sceneInterestSpiked });

  // Sort by score ascending — weakest first
  entries.sort((a, b) => a.score - b.score);

  const totalDeficit = entries.reduce(
    (sum, e) => sum + Math.max(0, qualityThreshold - e.score), 0
  );

  const priorities: EditPriority[] = entries.map((entry, index) => {
    const deficit = Math.max(0, qualityThreshold - entry.score);
    // Intensity is proportional to deficit, normalized to 0-1
    // Weakest dimension gets highest intensity
    const editIntensity = qualityThreshold > 0
      ? Math.min(1.0, deficit / qualityThreshold)
      : 0;

    return {
      dimension: entry.key,
      score: entry.score,
      deficit: Math.round(deficit * 100) / 100,
      editIntensity: Math.round(editIntensity * 100) / 100,
      editOperations: deficit > 0 ? EDIT_OPERATIONS[entry.key] : [],
      priorityRank: index + 1,
    };
  });

  const weakest = entries[0];
  const strongest = entries[entries.length - 1];

  const deficitDims = priorities.filter(p => p.deficit > 0);
  const summary = deficitDims.length === 0
    ? 'All dimensions meet quality threshold — minimal editing needed.'
    : `${deficitDims.length} dimension${deficitDims.length > 1 ? 's' : ''} below threshold. ` +
      `Targeting ${weakest.key} first (score ${weakest.score.toFixed(1)}, ` +
      `deficit ${priorities[0].deficit.toFixed(1)}, ` +
      `intensity ${(priorities[0].editIntensity * 100).toFixed(0)}%).`;

  return {
    priorities,
    totalDeficit: Math.round(totalDeficit * 100) / 100,
    weakestDimension: weakest.key,
    strongestDimension: strongest.key,
    summary,
  };
}

// Patent: Claim 5(d) — Tier-based edit intensity scaling.
// Higher subscription tiers unlock stronger editing (free=0.3, silver=0.6, gold=1.0).
/**
 * Apply edit intensity scaling based on user tier.
 * Higher tiers unlock stronger editing.
 */
export function scaleEditIntensity(
  plan: EditPlan,
  maxIntensity: number, // 0.3 for lowkey, 0.6 for balanced, 1.0 for highkey
): EditPlan {
  return {
    ...plan,
    priorities: plan.priorities.map(p => ({
      ...p,
      editIntensity: Math.min(p.editIntensity, maxIntensity),
    })),
  };
}

// ─── Diagnostic Card Generation ─────────────────────────────
// Patent: Claim 5 — Score-card-driven editing pipeline.
// The diagnostic card IS the edit recipe: score → diagnose → specific suggestion → estimated new score.
// This unified chain has no identified prior art.
export interface PrismDiagnosticContext {
  ownerBaseline?: PrismOwnerBaseline | null;
  preferenceVector?: PrismPreferenceVector | null;
}

export function generateDiagnosticCard(
  photo: { dimensions: DimensionScores; compositeScore: number; detractorPenalty: number },
  qualityThreshold: number = 7.5,
  modeId?: string,
  prismContext?: PrismDiagnosticContext,
): DiagnosticCardResult {
  const blockingFactors: DiagnosticBlockingFactor[] = [];
  const suggestions: DiagnosticSuggestion[] = [];

  const dimEntries: Array<{ key: DimensionKey; score: number | null }> = [
    { key: 'portraitComposition', score: photo.dimensions.portraitComposition },
    { key: 'lighting', score: photo.dimensions.lighting },
    { key: 'composition', score: photo.dimensions.composition },
    { key: 'sceneInterest', score: photo.dimensions.sceneInterestSpiked },
  ];

  // Detect if this is likely a selfie/portrait (has face, composition is low)
  const hasFace = photo.dimensions.portraitComposition !== null;
  const lowComposition = photo.dimensions.composition < 4.0;
  const isSelfieContext = hasFace && lowComposition;

  // Mode-specific suggestion maps for platform-optimized diagnostics
  const MODE_SUGGESTIONS: Record<string, Partial<Record<DimensionKey, string[]>>> = {
    social: {
      portraitComposition: [
        'Natural smile photographs best — avoid posed or forced expressions',
        'Eye contact with camera builds connection',
        'Step back slightly — headshot with visible shoulders performs best',
      ],
      lighting: [
        'Soft, warm lighting is most appealing for profile photos',
        'Warm white balance +200K for golden tone',
        'Avoid direct flash — natural light is ideal',
      ],
      composition: [
        'Clean background shows intentionality — avoid clutter',
        'Simple, non-distracting backgrounds perform best on social profiles',
        'Position yourself using rule-of-thirds for visual interest',
      ],
    },
    instagram: {
      lighting: [
        'Warm color grading increases engagement',
        'Golden hour lighting transforms any scene',
        'Increase contrast +10 for scroll-stopping impact',
      ],
      sceneInterest: [
        'Vibrant but not oversaturated performs best',
        'Strong composition draws the eye — use leading lines',
        'Interesting backgrounds add scroll-stop factor',
      ],
    },
    travel: {
      lighting: [
        'Golden hour lighting transforms travel photos',
        'Lift shadows +15 to show detail in landscapes',
        'Warm tones evoke the feeling of being there',
      ],
      sceneInterest: [
        'Wide framing captures more context and atmosphere',
        'Include yourself naturally — don\u2019t block the landmark',
        'Boost vibrance +15 for richer colors',
      ],
    },
    event: {
      portraitComposition: [
        'Candid moments score higher than posed group shots',
        'Capture expressions during toasts, dances, and reactions',
        'Group photos: make sure all faces are visible and eyes are open',
      ],
      lighting: [
        'Warm venue lighting adds atmosphere — embrace it',
        'Avoid direct flash when possible — bounce or diffuse',
        'Balance ambient light with fill for warm, natural look',
      ],
      composition: [
        'Embrace the energy — busy scenes tell the story',
        'Focus on one or two central subjects in group moments',
        'Step back to include the full scene and decorations',
      ],
    },
    sports: {
      portraitComposition: [
        'Peak action moments score highest — ball release, finish line, celebration',
        'Facial expression is secondary — body position tells the story',
        'Anticipate the moment — pre-focus on the action zone',
      ],
      lighting: [
        'Stadium lighting is challenging — exposure consistency matters most',
        'Lift shadows +15 to recover detail under harsh overhead lights',
        'Embrace dramatic lighting for emotional impact',
      ],
      composition: [
        'Subject isolation: athlete sharp against blurred background',
        'Use leading lines (track lanes, field markings) to draw the eye',
        'Leave space in the direction of motion for dynamic tension',
      ],
      sceneInterest: [
        'Panning with the action creates dynamic motion blur',
        'High-energy moments score highest — celebration, impact, peak effort',
        'Freeze the decisive moment — timing is everything',
      ],
    },
    realEstate: {
      lighting: [
        'Ensure even lighting across the room — no dark corners',
        'Shoot during daytime with all interior lights on for warmth',
        'Bracket exposures to capture both window views and interior detail',
      ],
      composition: [
        'Shoot from corners to maximize visible floor and ceiling space',
        'Level the camera at waist height for natural perspective',
        'Include two walls and the floor for depth — avoid flat, head-on shots',
      ],
      sceneInterest: [
        'Stage key rooms: fresh flowers, set dining table, plumped cushions',
        'Remove personal items — buyers need to imagine their own life there',
        'Capture the view from the best window as a hero shot',
      ],
    },
    professional: {
      portraitComposition: [
        'Confident, approachable expression — slight smile, direct gaze',
        'Head and shoulders framing with slight head turn',
        'Ensure clean jawline — slight chin-down angle helps',
      ],
      lighting: [
        'Even, soft lighting from a window or studio softbox',
        'Avoid harsh shadows under eyes — use fill light or reflector',
        'Cool-neutral white balance for corporate environments',
      ],
      composition: [
        'Neutral, uncluttered background — solid color or subtle texture',
        'Center framing with headroom — professional and direct',
        'Consistent style across all team headshots',
      ],
    },
  };

  // Merge mode-specific suggestions with defaults
  const modeSuggestions = modeId ? MODE_SUGGESTIONS[modeId] : undefined;

  const SUGGESTION_MAP: Record<DimensionKey, string[]> = {
    portraitComposition: modeSuggestions?.portraitComposition ?? [
      'Try a natural, relaxed expression — genuine smiles score highest',
      'Adjust distance: hold phone at arm\u2019s length for ideal face-to-frame ratio',
      'Soften background with portrait mode blur +40%',
    ],
    lighting: modeSuggestions?.lighting ?? [
      'Reduce highlights -20, lift shadows +15',
      'Warm white balance +200K for golden tone',
      'Increase contrast +10 for more dynamic range',
    ],
    composition: modeSuggestions?.composition ?? (isSelfieContext ? [
      'Hold phone further back for better headroom and body framing',
      'Find a cleaner, simpler background',
      'Position eyes in upper third of frame — slight angle helps',
    ] : [
      'Crop to rule-of-thirds alignment on subject',
      'Level horizon -2 degrees',
      'Remove distracting elements from edges',
    ]),
    sceneInterest: modeSuggestions?.sceneInterest ?? [
      'Boost vibrance +15 for richer colors',
      'Apply clarity enhancement +20 for detail pop',
      'Add subtle vignette to draw focus to center',
    ],
  };

  let totalImpact = 0;

  for (const { key, score } of dimEntries) {
    if (score === null) continue;
    const deficit = qualityThreshold - score;
    if (deficit > 0) {
      const impactPoints = Math.round(deficit * 10);
      totalImpact += impactPoints;

      const descriptions: Record<DimensionKey, string> = {
        portraitComposition: score < 3 ? 'Expression or framing significantly hurting score' : 'Portrait composition could be stronger',
        lighting: score < 4 ? 'Harsh shadows or flat lighting detected' : 'Lighting is acceptable but not optimal',
        composition: isSelfieContext
          ? (score < 3 ? 'Selfie framing is too tight — step back for headroom' : 'Selfie framing could improve — try a cleaner background')
          : (score < 4 ? 'Cluttered or unbalanced framing' : 'Framing could be tighter'),
        sceneInterest: score < 4 ? 'Low visual interest in scene' : 'Scene lacks a strong focal point',
      };

      blockingFactors.push({
        dimension: key,
        impactPoints,
        description: descriptions[key],
      });

      const specificSuggestions = SUGGESTION_MAP[key];
      for (const action of specificSuggestions) {
        suggestions.push({
          action,
          specificity: 'specific',
          estimatedScoreDelta: Math.round(deficit * 3) / 10,
        });
      }
    }
  }

  // Add detractor-specific suggestions
  if (photo.detractorPenalty > 0.1) {
    blockingFactors.push({
      dimension: 'composition',
      impactPoints: Math.round(photo.detractorPenalty * 10),
      description: 'Background clutter or crowd detected',
    });
    suggestions.push({
      action: 'Apply background blur or use AI background removal',
      specificity: 'specific',
      estimatedScoreDelta: photo.detractorPenalty * 0.6,
    });
  }

  const estimatedDelta = Math.min(totalImpact * 0.6 / 10, 2.0);
  const estimatedNewScore = Math.min(10, photo.compositeScore + estimatedDelta);

  const dimensionScores: Record<DimensionKey, number | null> = {
    portraitComposition: photo.dimensions.portraitComposition,
    lighting: photo.dimensions.lighting,
    composition: photo.dimensions.composition,
    sceneInterest: photo.dimensions.sceneInterestSpiked,
  };

  // PRISM: Owner-relative summary override
  let personalizedSummary: string | undefined;
  if (prismContext?.ownerBaseline && prismContext.ownerBaseline.photoCount >= 10) {
    const baseline = prismContext.ownerBaseline;
    const range = Math.max(1.0, baseline.bestCompositeScore - baseline.avgPortraitComposition);
    const zScore = (photo.compositeScore - baseline.avgPortraitComposition) / range;

    if (zScore >= 1.5) {
      personalizedSummary = 'One of your best photos — keep this one.';
    } else if (zScore >= 0.75) {
      personalizedSummary = 'Above your usual quality — nice work.';
    } else if (zScore <= -1.0 && blockingFactors.length > 0) {
      personalizedSummary = `Below your usual — you've taken better. Top issue: ${blockingFactors[0].description}.`;
    }
  }

  // PRISM: Preference-aware style hints on suggestions
  if (prismContext?.preferenceVector && prismContext.preferenceVector.confidence > 0.3) {
    const pv = prismContext.preferenceVector;
    for (const suggestion of suggestions) {
      const action = suggestion.action.toLowerCase();
      if ((action.includes('white balance') || action.includes('warm')) && pv.warmthPreference > 0.2) {
        suggestion.action += ' (warming usually matches your style)';
      } else if ((action.includes('contrast') || action.includes('dynamic range')) && pv.contrastPreference > 0.2) {
        suggestion.action += ' (you tend to prefer punchier contrast)';
      } else if (action.includes('vibrance') && pv.saturationPreference > 0.2) {
        suggestion.action += ' (vibrant tones match your preference)';
      } else if ((action.includes('background') || action.includes('blur')) && pv.backgroundPreference > 0.2) {
        suggestion.action += ' (clean backgrounds match your style)';
      }
    }
  }

  const defaultSummary = blockingFactors.length === 0
    ? 'Great photo — all dimensions meet quality threshold.'
    : `${blockingFactors.length} area${blockingFactors.length > 1 ? 's' : ''} holding this photo back. Top priority: ${blockingFactors[0].description}.`;

  return {
    overallScore: photo.compositeScore,
    dimensionScores,
    blockingFactors,
    suggestions,
    estimatedNewScore: Math.round(estimatedNewScore * 10) / 10,
    summary: personalizedSummary ?? defaultSummary,
  };
}
